/**
 * @file getAPIVersion.ts
 * @description This module handles API versioning for the SDK. The SDK uses API version '2.4'.
 * If no version is provided, it will default to '2.4'.
 */

/**
 * Enum for available API versions.
 * @readonly
 */
export const APIVersionTypes = {
  V_2_4: '2.4',
} as const;

/**
 * @description Defines the API version that the SDK should use.
 * The SDK uses API version "2.4" with updated functionalities.
 *
 * If this value is not set, the SDK will default to "2.4".
 */
export type APIVersionOption = '2.4';

/**
 * Type for API version.
 */
export type APIVersion = (typeof APIVersionTypes)[keyof typeof APIVersionTypes];

/**
 * Default API version to use when none is specified by the merchant.
 * Uses API version '2.4'.
 */
const defaultVersion: APIVersion = APIVersionTypes.V_2_4;

/**
 * Current API version set by the merchant or defaults to the defaultVersion.
 */
let currentAPIVersion: APIVersion = defaultVersion;

/**
 * Sets the API version for the SDK.
 * If an invalid version is provided, it defaults to the predefined defaultVersion.
 * In development mode, a warning is logged if no version is explicitly set.
 */
export function setAPIVersion(version: APIVersion | undefined): void {
  //In case we introduce more versions, we should provide a little console warner:
  // if (!version) {
  //   console.warn(
  //     `[Primer SDK] No API version was explicitly set. Defaulting to '${defaultVersion}'.`,
  //   );
  // }
  currentAPIVersion = version ?? defaultVersion;
}

/**
 * Gets the current API version.
 */
export function getAPIVersion(): APIVersion {
  return currentAPIVersion;
}
